import React from 'react'
import PropTypes from 'prop-types'
import { useInView } from 'react-intersection-observer'
import loadable from '@loadable/component'
import cn from 'classnames'
import useLocale from 'js/hooks/useLocale'

const CardBlock = loadable(() => import('./blocks/CardBlock'))
const CardsBlock = loadable(() => import('./blocks/CardsBlock'))
const CtaBlock = loadable(() => import('./blocks/CtaBlock'))
const DownloadCtaBlock = loadable(() => import('./blocks/DownloadCtaBlock'))
const ImageBlock = loadable(() => import('./blocks/ImageBlock'))
const ListWithIconsBlock = loadable(() => import('./blocks/ListWithIconsBlock'))
const SlideshowBlock = loadable(() => import('./blocks/SlideshowBlock'))
const TextBlock = loadable(() => import('./blocks/TextBlock'))
const ImageTextBlock = loadable(() => import('./blocks/ImageTextBlock'))
const LinkableImageTextBlock = loadable(() =>
  import('./blocks/LinkableImageTextBlock'),
)
const AppButtonBlock = loadable(() => import('./blocks/AppButtonBlock'))
const TextHero = loadable(() => import('./blocks/TextHero'))
const NumberedCardsBlock = loadable(() => import('./blocks/NumberedCardsBlock'))
const ExternalLinksBlock = loadable(() => import('./blocks/ExternalLinksBlock'))
const VideoBlock = loadable(() => import('./blocks/VideoBlock'))
const QuoteHeroBlock = loadable(() => import('js/components/UserCase/UserCase'))
const NewsBlock = loadable(() => import('js/components/NewsBlock/NewsBlock'))
const WidgetBlock = loadable(() =>
  import('js/components/WidgetBlock/WidgetBlock'),
)

function lazy(component) {
  // eslint-disable-next-line react/prop-types
  return function LazyBlock({ inView, ...props }) {
    const Component = component
    if (!inView) return null
    return <Component {...props} />
  }
}

const blocks = {
  CardBlockRecord: CardBlock,
  CardsBlockRecord: CardsBlock,
  CtaBlockRecord: CtaBlock,
  DownloadCtaBlockRecord: DownloadCtaBlock,
  ImageBlockRecord: ImageBlock,
  ImageTextBlockRecord: ImageTextBlock,
  LinkableImageTextBlockRecord: LinkableImageTextBlock,
  ListPageBlockRecord: ListWithIconsBlock,
  SlideshowBlockRecord: lazy(SlideshowBlock),
  TextBlockRecord: TextBlock,
  AppButtonBlockRecord: AppButtonBlock,
  TextHeroRecord: TextHero,
  NumberedCardsBlockRecord: NumberedCardsBlock,
  VideoBlockRecord: VideoBlock,
  QuoteHeroRecord: QuoteHeroBlock,
  NewsBlockRecord: NewsBlock,
  WidgetRecord: WidgetBlock,
  ExternalLinksBlockRecord: ExternalLinksBlock,
}

export default function PageBlock({ type, ...props }) {
  const [inViewRef, inView] = useInView({ triggerOnce: true })
  const Block = blocks[type]
  const { language } = useLocale()

  if (type === 'NewsBlockRecord' && language !== 'en') return null

  return (
    <div
      className={cn(
        {
          ' mx-auto': type !== 'TextHeroRecord' && type !== 'QuoteHeroRecord',
          'lg:max-w-1280': type === 'NewsBlockRecord',
        },
        props.width === 'narrow' && 'max-w-968',
        props.width === 'medium' && 'max-w-1440',
      )}
      ref={inViewRef}
    >
      <Block {...props} inView={inView} />
    </div>
  )
}

PageBlock.propTypes = {
  type: PropTypes.oneOf(Object.keys(blocks)),
  width: PropTypes.oneOf(['wide', 'narrow', '']),
}
